// Eskom PARCC — Rule Book (static governance reference, admin only).
//
// The monolith's `#page-rules`, rebuilt as a read-only reference rendered from
// the domain — NO data fetching. It documents how a finding is classified and
// how the Compliance Evaluation Sheet (CES) maps a reviewer's status to a
// register level. Sections, in order:
//
//   1. Decision hierarchy — the anchor-first classification steps (faithful
//      static reproduction of the monolith's governance prose, which lives in
//      copy rather than in the domain data).
//   2. CES status → register level — DERIVED from the domain: each real
//      `CesStatus` run through the live `cesMapStatusToRegister` (via
//      `cesStatusRegisterRows`). This is the canonical, drift-proof mapping.
//   3. Procurement routes — the 8 CES routes from `CES_ROUTE_DEFS` (via
//      `routeSummaries`): each route's name, criterion count and stages.
//   4. Classification matrix — finding theme → anchor type → primary
//      classification (faithful static reproduction of the monolith matrix).
//   5. Required output fields + the reviewer-accountability rule.
//
// Where content is domain-sourced it is rendered from the live domain so it
// cannot drift; where the monolith carried governance *prose* with no domain
// equivalent, a faithful short static version is reproduced (per the task).
import {
  DataTable,
  ComplianceBadge,
  CesStatusBadge,
  Card,
  type Column,
} from '../../components/ui';
import { cesStatusRegisterRows, routeSummaries, type CesStatusRegisterRow, type RouteSummary } from './ruleBookData';
import { WORKING_PAPER_TEMPLATES } from '../../domain/workingPapers/workingPaperTemplates.generated';
import { suggestComplianceForCriterion } from '../../domain/workingPapers/resolveTemplate';
import type { DivisionFamily, WpCriterion } from '../../domain/workingPapers/templateTypes';

// --- Static governance prose (no domain equivalent; faithful to the monolith) ---

interface DecisionStep {
  step: number;
  question: string;
  decision: string;
}

const DECISION_HIERARCHY: readonly DecisionStep[] = [
  { step: 1, question: 'Does the finding state no active gap, resolved, compliant, or closed?', decision: 'Compliant' },
  {
    step: 2,
    question:
      'Is the requirement anchored in PFMA, National Treasury, the Constitution, legislation, reporting to NT/AGSA, statutory returnables, validity, tax/CSD, or competitive-procurement principles?',
    decision: 'Possible Non-Compliance',
  },
  {
    step: 3,
    question:
      'Is the issue internal to Eskom procedure, governance workflow, CFT/DOI/NDA process, evaluation pack, mandate, minutes, or file completeness?',
    decision: 'Non-Conformance',
  },
  {
    step: 4,
    question:
      'Is the issue only filing, taxonomy, EDMS upload quality, duplicate/missing evidence location, or document-reference quality?',
    decision: 'Non-Conformance - Administrative Control Weakness',
  },
  { step: 5, question: 'Is the wording unclear or the evidence insufficient?', decision: 'Pending Review' },
];

interface MatrixRow {
  theme: string;
  anchorType: string;
  classification: string;
  impact: string;
  severity: string;
}

const CLASSIFICATION_MATRIX: readonly MatrixRow[] = [
  {
    theme: 'No proof of NT/AGSA reporting for single/sole/urgent/emergency procurement',
    anchorType: 'National Treasury / PFMA',
    classification: 'Possible Non-Compliance',
    impact: 'Reporting Failure; Possible IE',
    severity: 'Critical',
  },
  {
    theme: 'No proof of publication of successful/unsuccessful tenders on required portals',
    anchorType: 'PFMA / National Treasury / Transparency',
    classification: 'Possible Non-Compliance',
    impact: 'Transparency Failure; Possible IE',
    severity: 'High',
  },
  {
    theme: 'Validity period exceeded, award outside validity, or no extension proof',
    anchorType: 'PFMA / procurement fairness',
    classification: 'Possible Non-Compliance',
    impact: 'Possible IE',
    severity: 'Critical',
  },
  {
    theme: 'No statutory documents such as CSD, tax, BBBEE, SBD/MBD declaration',
    anchorType: 'Statutory / PFMA',
    classification: 'Possible Non-Compliance',
    impact: 'Supplier Compliance Failure',
    severity: 'High',
  },
  {
    theme: 'No CFT appointment letters, DOI, NDA, evaluation report, minutes or negotiation pack',
    anchorType: 'Internal governance / procedure',
    classification: 'Non-Conformance',
    impact: 'Governance Weakness',
    severity: 'Moderate',
  },
  {
    theme: 'EDMS filing, taxonomy, wrong folder, information dump, missing upload',
    anchorType: 'Administrative control',
    classification: 'Non-Conformance - Administrative Control Weakness',
    impact: 'Documentation Weakness',
    severity: 'Low / Moderate',
  },
];

const OUTPUT_FIELDS: readonly { name: string; detail: string }[] = [
  {
    name: 'Anchor type',
    detail:
      'Legislation, PFMA, National Treasury, Constitution, Statutory Requirement, Procurement Instruction, Internal Policy, Procedure, Governance, Administrative Control.',
  },
  {
    name: 'Primary classification',
    detail:
      'Possible Non-Compliance, Non-Conformance, Non-Conformance - Administrative Control Weakness, Compliant, Pending Review, or Not Applicable.',
  },
  {
    name: 'Impact flag',
    detail:
      'Possible IE, Reporting Failure, Transparency Failure, Governance Weakness, Documentation Weakness, Supplier Compliance Failure, Financial Exposure, or No Immediate Impact.',
  },
  {
    name: 'Severity & escalation',
    detail:
      'Critical and High findings require management attention; PFMA, Treasury, Constitution and reporting failures require escalation.',
  },
];

export function RulesPage(): React.JSX.Element {
  const cesRows = cesStatusRegisterRows();
  const routes = routeSummaries();

  // The per-mechanism working-paper criteria, derived LIVE from the generated
  // spec dataset so the Rule Book can never drift from what the capture flow uses.
  const mechanismGroups = (Object.keys(WORKING_PAPER_TEMPLATES) as DivisionFamily[]).flatMap((family) =>
    Object.values(WORKING_PAPER_TEMPLATES[family]).map((tpl) => ({
      family,
      mechanism: tpl.mechanism,
      criteria: tpl.criteria,
      suggested: tpl.criteria.filter((c) => suggestComplianceForCriterion(c)).length,
    })),
  );

  const criterionColumns: Column<WpCriterion>[] = [
    { key: 'code', header: '#', render: (c) => c.code ?? String(c.seq) },
    { key: 'label', header: 'Sub-criteria', render: (c) => c.label },
    { key: 'anchors', header: 'Anchors', render: (c) => c.anchors.join('; ') || '—' },
    {
      key: 'suggested',
      header: 'Suggested level',
      render: (c) => {
        const s = suggestComplianceForCriterion(c);
        return s ? <ComplianceBadge level={s} /> : <span className="small">Reviewer decides</span>;
      },
    },
  ];

  const cesColumns: Column<CesStatusRegisterRow>[] = [
    {
      key: 'status',
      header: 'CES status (reviewer selects)',
      render: (r) => <CesStatusBadge status={r.status} />,
    },
    {
      key: 'register',
      header: 'Register compliance level',
      render: (r) => <ComplianceBadge level={r.register} />,
    },
  ];

  const routeColumns: Column<RouteSummary>[] = [
    { key: 'name', header: 'Procurement route', render: (r) => r.name },
    { key: 'criterionCount', header: 'Criteria', align: 'right', render: (r) => r.criterionCount },
    {
      key: 'stages',
      header: 'Assessment stages',
      render: (r) => (
        <ol style={{ margin: 0, paddingLeft: 18 }}>
          {r.stages.map((s) => (
            <li key={s} className="small">
              {s}
            </li>
          ))}
        </ol>
      ),
    },
  ];

  return (
    <>
      <div className="callout">
        <strong>Anchor-first rule engine</strong>
        <div className="small">
          The rulebook separates the legal/procedural anchor from the consequence. The primary
          classification is driven by the authority breached: legislation, PFMA, National Treasury,
          the Constitution and statutory requirements produce <strong>Possible Non-Compliance</strong>;
          internal procedures, governance steps and administrative controls produce{' '}
          <strong>Non-Conformance</strong>. Possible IE is retained as an impact flag, not as the
          primary classification.
        </div>
      </div>

      <Card title="Decision hierarchy" subtitle="The order in which a finding is classified.">
        <DataTable
          columns={[
            { key: 'step', header: 'Step', align: 'right', render: (s: DecisionStep) => s.step },
            { key: 'question', header: 'Question', render: (s: DecisionStep) => s.question },
            { key: 'decision', header: 'Decision', render: (s: DecisionStep) => <ComplianceBadge level={s.decision} /> },
          ]}
          rows={[...DECISION_HIERARCHY]}
          getRowKey={(s) => s.step}
        />
      </Card>

      <Card
        title="CES status → register level"
        subtitle="How each Compliance Evaluation Sheet status maps to a register compliance level (cesMapStatusToRegister)."
      >
        <DataTable columns={cesColumns} rows={cesRows} getRowKey={(r) => r.status || '(unset)'} />
      </Card>

      <Card
        title="Procurement routes"
        subtitle={`The ${routes.length} CES evaluation routes and the stages each one walks through.`}
      >
        <DataTable columns={routeColumns} rows={routes} getRowKey={(r) => r.name} />
      </Card>

      <Card
        title="Per-mechanism working papers"
        subtitle="The audit criteria each mechanism's working paper applies (NTCSA uses its own set; all other divisions share the Other-Divisions set), with the legislation/PFMA/Eskom-policy anchor and the rule-book suggested compliance level per criterion. Reviewers see these suggestions when populating a working paper."
      >
        {mechanismGroups.map((g) => (
          <details key={`${g.family}-${g.mechanism}`} style={{ marginBottom: 8 }}>
            <summary>
              <strong>{g.family}</strong> · {g.mechanism}{' '}
              <span className="small">
                ({g.criteria.length} criteria · {g.suggested} with a suggested level)
              </span>
            </summary>
            <DataTable columns={criterionColumns} rows={g.criteria} getRowKey={(c) => String(c.seq)} />
          </details>
        ))}
      </Card>

      <Card title="Classification matrix" subtitle="Finding theme → anchor type → primary classification.">
        <DataTable
          columns={[
            { key: 'theme', header: 'Finding theme', render: (r: MatrixRow) => r.theme },
            { key: 'anchorType', header: 'Anchor type', render: (r: MatrixRow) => r.anchorType },
            {
              key: 'classification',
              header: 'Primary classification',
              render: (r: MatrixRow) => <ComplianceBadge level={r.classification} />,
            },
            { key: 'impact', header: 'Impact flag', render: (r: MatrixRow) => r.impact },
            { key: 'severity', header: 'Severity', render: (r: MatrixRow) => r.severity },
          ]}
          rows={[...CLASSIFICATION_MATRIX]}
          getRowKey={(r) => r.theme}
        />
      </Card>

      <Card title="Required output fields for every finding">
        <div className="rule-grid">
          {OUTPUT_FIELDS.map((f) => (
            <div className="rule" key={f.name}>
              <h3>{f.name}</h3>
              <p className="small">{f.detail}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Reviewer rule">
        <p className="small">
          The app suggests the classification, but the reviewer remains accountable for confirming
          the actual evidence, the applicable procurement instruction, the delegation of authority,
          and whether the matter must be escalated to Compliance, Legal, Finance, or governance
          structures.
        </p>
      </Card>
    </>
  );
}
